import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useFetchAuthorisation, useFetchUserData } from "../../utils/AppFunctions";
import config from "../../app-config";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "../../layouts/AdminLayout";
import { MdAdd, MdArrowBack, MdSave, MdDelete } from "react-icons/md";
import { create_new_account, delete_account, fetch_account_by_id, update_account } from "../../api/accountsAPI";
import { fetch_all_users_admin } from "../../api/usersAPI";
import Select from 'react-select'
import notify from "../../utils/IANotification";
import ModalLayout from "../../layouts/ModalLayout";
import SubNavbar from "../../components/ui/SubNavbar";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";

export default function AccountsFormPage() {
  const navigate = useNavigate();
  useFetchAuthorisation("user");
  useFetchUserData();

  let account_id = new URLSearchParams(window.location.search).get("account_id") || "new";
  const [activeTab, setActiveTab] = useState("general");
  const [deleteConfirmation, setDeleteConfirmation] = useState("")
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
  const [accountData, setAccountData] = useState({
    name: "",
    description: "",
    currency: [],
    member_user_ids: []
  })
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)

  const tabs = [
    { id: 'general', label: 'General Info & Members' },
    { id: 'currencies', label: 'Secondary Currencies' },
  ]

  const fetchAccountById = async () => {
    if (account_id === "new") return;
    let { data, error, message } = await fetch_account_by_id(account_id);
    if (error) return console.log("Error fetching account:", message);
    setAccountData({ ...data, currency: data.currency || [], member_user_ids: data.member_user_ids || [] });
  }

  const fetchPlatformUsers = async () => {
    setLoading(true)
    const data = await fetch_all_users_admin()
    if (data?.status === 200) {
      setUsers(data.data || [])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchAccountById();
    fetchPlatformUsers();
  }, [account_id]);

  const updateAccountById = async () => {
    if (account_id === "new") return;
    let { error, message } = await update_account(account_id, accountData);
    if (error) return notify({ type: "error", message: `Error updating account: ${message}` });
    await fetchAccountById();
    notify({ type: "success", message: "Account updated successfully" });
  }

  const createAccount = async () => {
    let { data, error, message } = await create_new_account(accountData);
    if (error) return notify({ type: "error", message: `Error creating account: ${message}` });
    notify({ type: "success", message: "Account created successfully" });
    navigate(`/admin/accounts?account_id=${data.id}`);
  }

  const deleteAccountById = async () => {
    let { error, message } = await delete_account(account_id);
    if (error) return notify({ type: "error", message: `Error deleting account: ${message}` });
    notify({ type: "success", message: "Account deleted successfully" });
    navigate(`/admin/accounts`);
  }

  return (
    <AdminLayout
      navSelected={"Accounts"}
      headerText={account_id === "new" ? "New Financial Account" : "Edit Financial Account"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/accounts')}>
              Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900">
              {account_id === "new" ? "Create Account" : accountData.name || "Edit Account"}
            </h1>
          </div>
          <div className="flex gap-2">
            {account_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={() => setShowDeleteConfirmationModal(true)}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (account_id === "new") createAccount();
              else updateAccountById();
            }}>
              {account_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <ModalLayout modalTitle={`Delete Account: ${accountData?.name || ""}`}
        isVisible={showDeleteConfirmationModal} setIsVisible={setShowDeleteConfirmationModal}>
        <div className="space-y-4">
          <p className="text-xs text-gray-600">
            There is no undo for this action. Permanently deleting this account will also remove all associated claim records.
          </p>
          <p className="text-xs font-semibold text-gray-700">To confirm deletion, please type 'delete' below:</p>
          <input type="text" className="text-input" value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} />
          <div className="flex justify-end pt-2">
            <Button variant="danger" icon={MdDelete} onClick={() => {
              if (deleteConfirmation === "delete") {
                deleteAccountById()
              } else {
                notify({ type: "error", message: "Please type 'delete' to confirm deletion" })
              }
            }}>
              Confirm Delete
            </Button>
          </div>
        </div>
      </ModalLayout>

      {/* TAB 1: GENERAL INFO & MEMBERS */}
      {activeTab === "general" && (
        <div className="space-y-6">
          <Card title="Account Details" subtitle="Financial ledger account name and description.">
            <div className="space-y-6">
              <div className="input-container">
                <label className="input-label font-semibold">Account Name</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="e.g. Missions Fund"
                  value={accountData.name || ""}
                  onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Account Description</label>
                <textarea
                  className="textarea-input h-28"
                  placeholder="e.g. Budget dedicated for overseas mission trips and local outreach..."
                  value={accountData.description || ""}
                  onChange={(e) => setAccountData({ ...accountData, description: e.target.value })}
                />
              </div>
            </div>
          </Card>

          <Card title="Authorized Members" subtitle="Users authorized to submit claims for this financial account.">
            <div className="input-container">
              <label className="input-label font-semibold">Select Authorized Users</label>
              <Select
                value={users.filter(user => (accountData.member_user_ids || []).includes(user.id)).map(user => ({ value: user.id, label: `${user.first_name} ${user.last_name} (${user.email})` }))}
                options={users.map(user => ({ value: user.id, label: `${user.first_name} ${user.last_name} (${user.email})` }))}
                isMulti
                onChange={(selected) => setAccountData({ ...accountData, member_user_ids: (selected || []).map(s => s.value) })}
                placeholder="Select users authorized to submit claims..."
              />
            </div>
          </Card>
        </div>
      )}

      {/* TAB 2: SECONDARY CURRENCIES */}
      {activeTab === "currencies" && (
        <Card
          title="Secondary Exchange Currencies"
          subtitle="Configure secondary currencies and conversion rates relative to SGD."
          headerRight={
            <Button
              variant="secondary"
              size="sm"
              icon={MdAdd}
              onClick={() => {
                let newCurrency = [...(accountData.currency || []), { currency: "", rate: 1 }];
                setAccountData({ ...accountData, currency: newCurrency });
              }}
            >
              Add Currency
            </Button>
          }
        >
          {accountData.currency?.length > 0 ? (
            <div className="space-y-4">
              {accountData.currency.map((currencyItem, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
                  <div className="input-container">
                    <label className="input-label font-semibold">Currency Code</label>
                    <input
                      type="text"
                      className="text-input mb-0"
                      placeholder="e.g. USD"
                      value={currencyItem.currency || ""}
                      onChange={(e) => {
                        let newCurrency = [...accountData.currency];
                        newCurrency[index].currency = e.target.value;
                        setAccountData({ ...accountData, currency: newCurrency });
                      }}
                    />
                  </div>

                  <div className="input-container">
                    <label className="input-label font-semibold">Exchange Rate (vs SGD)</label>
                    <input
                      type="number"
                      step="0.0001"
                      className="text-input mb-0"
                      placeholder="e.g. 0.74"
                      value={currencyItem.rate || ""}
                      onChange={(e) => {
                        let newCurrency = [...accountData.currency];
                        newCurrency[index].rate = parseFloat(e.target.value) || 0;
                        setAccountData({ ...accountData, currency: newCurrency });
                      }}
                    />
                    <p className="text-[10px] text-gray-400 mt-1">1 SGD = {currencyItem.rate || 0} {currencyItem.currency || "---"}</p>
                  </div>

                  <div className="flex justify-end pb-3">
                    <Button
                      variant="danger"
                      size="sm"
                      icon={MdDelete}
                      onClick={() => {
                        let newCurrency = accountData.currency.filter((_, i) => i !== index);
                        setAccountData({ ...accountData, currency: newCurrency });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic py-6 text-center">No secondary exchange currencies added.</p>
          )}
        </Card>
      )}
    </AdminLayout>
  )
}